/**
 * Global mount that wires `useIdleLogout` (30-day idle timer for
 * regular users; DISABLED entirely for super admins & editors) to the
 * auth context. As of Sep 2026 the "Still there?" warning modal is
 * suppressed for every logged-in user per product feedback — the
 * server-side 30-day cookie still enforces the true session limit, so
 * the client-side nag was pure friction. The hook is still mounted for
 * regular users so it will silently refresh their token when they
 * return; only the visible modal is muted.
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";

export default function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Super admins / editors are trusted stewards of the platform —
  // never sign them out for inactivity. Their session persists until
  // they explicitly log out.
  const isPlatformAdmin = !!(
    user && (
      user.is_admin ||
      user.is_super_admin ||
      ["admin", "super_admin", "editor", "organization_admin"].includes(user.platform_role)
    )
  );

  const doLogout = useCallback(async () => {
    try { await logout?.(); } catch { /* ignore */ }
    toast.info("Your session was refreshed — please sign in again.");
    navigate("/", { replace: true });
  }, [logout, navigate]);

  useIdleLogout({
    // Disabled entirely for admins/editors. Regular users still get
    // signed out after 30 days of no interaction as a safety net.
    enabled: !!user && !isPlatformAdmin,
    onLogout: doLogout,
  });

  // Warning modal is intentionally NOT rendered. Users found the
  // "Still there? 01:00 unless you tap Stay signed in" popup annoying
  // and disruptive during ordinary tree-editing sessions. The 30-day
  // enforcement still runs silently in the hook above.
  return null;
}
